import { useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';

import { useDucatClient } from '@/context/DucatClientCtx';
import { canRegisterQuanta } from '@/lib/wallet-policy';
import { useAppStore } from '@/stores/useAppStore';

import type { QuantaHeaders } from './auth';
import { buildQuantaHeaders, getQuantaSignInIdentities, signQuantaAuth } from './auth';
import {
  type QuantaIdentity,
  type QuantaIdentityRole,
  quantaHeadersBelongToWallet,
  rememberQuantaIdentities,
  sameQuantaIdentity,
} from './quantaIdentity';
import { fetchQuantaLogin } from './useQuantaLogin';
import { useQuantaRegister } from './useQuantaRegister';

/**
 * To request a signature from wallet and set auth headers on DucatContext.
 */
export function useQuantaAuth() {
  const t = useTranslations('notifications');
  const tReview = useTranslations('quanta.connect');
  const { wallet, walletAddress, pubkey, client, walletType, accountRevision } = useDucatClient();
  const setQuantaHeaders = useAppStore((s) => s.setQuantaHeaders);
  const sessionRef = useRef({ wallet, walletType, accountRevision, mounted: true });
  sessionRef.current = { ...sessionRef.current, wallet, walletType, accountRevision };
  const signingAttemptRef = useRef(0);
  const stagedProofs = useRef(new WeakMap<QuantaHeaders, () => void>());
  useEffect(() => {
    sessionRef.current.mounted = true;
    return () => {
      sessionRef.current.mounted = false;
    };
  }, []);
  const { mutateAsync: register, isPending: isRegisterPending, error: registerError, reset: resetRegisterError } = useQuantaRegister();

  const signQuanta = useCallback(
    async ({
      publish = true,
      role = 'payment',
      identity,
      expectedUserId,
    }: {
      publish?: boolean;
      role?: QuantaIdentityRole;
      identity?: QuantaIdentity;
      expectedUserId?: string;
    } = {}) => {
      const attempt = ++signingAttemptRef.current;
      const previousHeaders = useAppStore.getState().quantaHeaders;
      const assertCurrent = () => {
        const current = sessionRef.current;
        if (
          !current.mounted ||
          current.wallet !== wallet ||
          current.walletType !== walletType ||
          current.accountRevision !== accountRevision ||
          signingAttemptRef.current !== attempt ||
          useAppStore.getState().quantaHeaders !== previousHeaders
        ) {
          throw new Error(t('errors.wallet.activeAccountUnavailable'));
        }
      };
      // Sign the wallet's live identity; a derived address may belong to another
      // address format or network and the wallet will correctly refuse to sign it.
      const provider = walletType === 'metamask' ? 'metamask' : walletType === 'unisat' ? 'unisat' : 'xverse';
      const readIdentities = () =>
        getQuantaSignInIdentities(provider, wallet ?? undefined).catch((error: unknown) => {
          if (error && typeof error === 'object' && 'action' in error && error.action === 'switch-network') {
            throw new Error(tReview('walletNetworkMismatch'));
          }
          throw error;
        });
      const identities = await readIdentities();
      if (!identities || !wallet) throw Error(t('errors.wallet.activeAccountUnavailable'));
      const active = identity
        ? Object.values(identities).find((candidate) => candidate && sameQuantaIdentity(candidate, identity))
        : identities[role];
      if (!active?.address || !active.publicKey) throw Error(t('errors.wallet.activeAccountUnavailable'));
      assertCurrent();
      rememberQuantaIdentities(wallet, accountRevision, identities);

      const authParams = await signQuantaAuth({
        address: active.address,
        pubkey: active.publicKey,
        client,
      });

      const headers = buildQuantaHeaders(authParams);
      const after = await readIdentities();
      assertCurrent();
      if (!after || !Object.values(after).some((candidate) => candidate && sameQuantaIdentity(candidate, active))) {
        throw Error(t('errors.wallet.activeAccountUnavailable'));
      }
      rememberQuantaIdentities(wallet, accountRevision, after);
      if (publish) {
        const verifiedUserId = expectedUserId ? (await fetchQuantaLogin(headers)).data.user.user_id : undefined;
        assertCurrent();
        if (expectedUserId && verifiedUserId !== expectedUserId) throw new Error('QUANTA_ACCOUNT_CONFLICT');
        if (
          previousHeaders &&
          quantaHeadersBelongToWallet(previousHeaders, wallet, accountRevision) &&
          !sameQuantaIdentity(active, { address: previousHeaders['x-address'], publicKey: previousHeaders['x-user-public-key'] })
        ) {
          // Role changes may resolve to different accounts. Keep the current proof
          // published until authenticated responses establish the canonical account.
          let previousUserId: string | undefined;
          try {
            previousUserId = (await fetchQuantaLogin(previousHeaders)).data.user.user_id;
          } catch (cause) {
            const response = (cause as { response?: { status?: number; data?: { code?: string } } }).response;
            if (
              ![400, 401].includes(response?.status ?? 0) &&
              !(response?.status === 404 && response.data?.code === 'NOT_FOUND') &&
              !(response?.status === 403 && response.data?.code === 'ACCOUNT_IDENTITY_UNVERIFIED')
            )
              throw cause;
          }
          assertCurrent();
          const nextUserId = verifiedUserId ?? (await fetchQuantaLogin(headers)).data.user.user_id;
          assertCurrent();
          if (previousUserId && previousUserId !== nextUserId) throw new Error('QUANTA_ACCOUNT_CONFLICT');
        }
        if (expectedUserId) {
          const currentIdentities = await readIdentities();
          assertCurrent();
          if (
            !currentIdentities ||
            !Object.values(currentIdentities).some((candidate) => candidate && sameQuantaIdentity(candidate, active))
          ) {
            throw Error(t('errors.wallet.activeAccountUnavailable'));
          }
        }
        setQuantaHeaders(headers);
      } else stagedProofs.current.set(headers, assertCurrent);

      return headers;
    },
    [accountRevision, client, setQuantaHeaders, t, tReview, wallet, walletType],
  );

  const registerUser = useCallback(
    async (headers: QuantaHeaders, referralCode: string) => {
      if (!canRegisterQuanta(sessionRef.current.walletType)) throw new Error(tReview('metamaskRegistrationRequired'));
      if (
        headers &&
        walletAddress &&
        pubkey &&
        quantaHeadersBelongToWallet(headers, sessionRef.current.wallet, sessionRef.current.accountRevision, 'payment')
      ) {
        const assertCurrent = stagedProofs.current.get(headers);
        assertCurrent?.();
        let userData: Awaited<ReturnType<typeof register>> | undefined;
        try {
          userData = await register({ headers, referralCode });
        } catch (error) {
          const e = error as { status?: number; response?: { data?: { error?: string } } };
          if (e.status !== 409 || e.response?.data?.error !== 'User with this wallet or public key already exists') throw error;
        }
        if (assertCurrent) {
          assertCurrent();
          stagedProofs.current.delete(headers);
          setQuantaHeaders(headers);
        }
        return userData;
      } else {
        throw Error(t('errors.wallet.notReady'));
      }
    },
    [pubkey, register, setQuantaHeaders, t, tReview, walletAddress],
  );

  return { signQuanta, registerUser, isRegisterPending, registerError, resetRegisterError };
}
